import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Plus, FolderKanban, Boxes, Truck, AlertTriangle } from "lucide-react";
import { get } from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/common/States";
import { fmtDate, num, pct } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useMediaQuery } from "@/hooks/use-media-query";
import { PortfolioMap } from "@/components/map/PortfolioMap";
import { MapPreviewCard } from "@/components/map/MapPreviewCard";
import { MapLinkChip, MapLegend, MapVignette } from "@/components/map/MapChrome";
import { useOpsMap } from "@/components/map/OpsMap";
import { readStoredLayer, storeLayer } from "@/components/map/mapLayers";
import { useBrief, AiBriefLine } from "@/components/dashboard/AiBrief";
import { StatStrip } from "@/components/dashboard/StatStrip";
import { PriorityPanel } from "@/components/dashboard/PriorityPanel";
import { WeekTimeline } from "@/components/dashboard/WeekTimeline";
import { FleetSummary, CapacitySummary, PipelineSummary, QuietLink } from "@/components/dashboard/Summaries";
import { CampCards } from "@/components/innkvartering/CampList";
import { AccommodationBrief } from "@/components/innkvartering/AccommodationBits";

/** Camp operators: their camps with occupancy, next 7 days and one-tap access to Romkart / I dag / Beredskap (server-scoped). */
const ScopedCamps = () => {
  const { user } = useAuth();
  const q = useQuery({ queryKey: ["ink", "portfolio", user?.id], queryFn: () => get("/innkvartering/portfolio"), refetchInterval: 120_000 });
  if (q.isLoading) return <LoadingState className="py-10" text="Henter campene dine…" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} className="py-8" />;
  const d = q.data;
  if (!d?.camps?.length) return null;
  return (
    <section className="mt-8 max-w-3xl" data-testid="scoped-camps">
      <header className="flex items-baseline justify-between">
        <h2 className="text-[13.5px] font-semibold leading-5 tracking-tight">Dine camper</h2>
        <span className="font-mono text-[12px] tabular text-muted-foreground">{d.totals.on_site} innsjekket · {d.totals.arrivals_7d} inn · {d.totals.departures_7d} ut neste 7 d</span>
      </header>
      <AccommodationBrief sentences={d.brief} compact className="mt-3" testId="scoped-camps-brief" meta={null} />
      <CampCards camps={d.camps} className="mt-3 border-t border-border/70" />
    </section>
  );
};

const LEGEND = [
  { label: "Prosjekt", dot: "bg-card ring-2 ring-emerald-500" },
  { label: "Depot", dot: "bg-navy" },
  { label: "Transport", dot: "bg-primary" },
];

/** Eases the camera to a location picked from the priority list (keeps the floating panel clear of the target). */
const CameraFocus = ({ target, tick, offsetX = 0 }) => {
  const { map } = useOpsMap();
  useEffect(() => {
    if (!map || !target) return;
    map.easeTo({ center: [target.lng, target.lat], zoom: Math.max(map.getZoom(), 9.6), offset: [offsetX, 0], duration: 800, essential: true });
  }, [tick]);
  return null;
};

/**
 * Oversikt – the operations control centre. Greeting + AI brief, four numbers, one dominant map with the
 * prioritised work floating on it, the week as an operations board, and three quiet visual summaries.
 */
export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, can } = useAuth();
  // Project-scoped (external) users get a reduced dashboard – no company-wide map/brief calls (the server rejects them anyway).
  const scoped = Array.isArray(user?.scope_project_ids) && user.scope_project_ids.length > 0;
  const dash = useQuery({ queryKey: ["dashboard"], queryFn: () => get("/dashboard"), refetchInterval: 120_000 });
  const mapQ = useQuery({ queryKey: ["map-overview", "today"], queryFn: () => get("/map/overview"), refetchInterval: 120_000, enabled: can("projects:read") && !scoped });
  const brief = useBrief(can("dashboard:read") && !scoped);
  const [selectedId, setSelectedId] = useState(null);
  const [focus, setFocus] = useState({ loc: null, tick: 0 });
  const [layer, setLayer] = useState(readStoredLayer);
  const wide = useMediaQuery("(min-width: 1280px)");
  const changeLayer = (id) => {
    setLayer(id);
    storeLayer(id);
  };

  const d = dash.data;
  const m = mapQ.data;
  const locatable = useMemo(() => new Set((m?.locations || []).map((l) => l.project?.id).filter(Boolean)), [m]);
  const weekEvents = useMemo(() => {
    if (!d || d.scoped || !d.today || !Array.isArray(d.events)) return [];
    const end = new Date(d.today);
    end.setDate(end.getDate() + 6);
    const endIso = end.toISOString().slice(0, 10);
    return d.events.filter((e) => e.date >= d.today && e.date <= endIso);
  }, [d]);

  if (dash.isLoading || (mapQ.isLoading && can("projects:read") && !scoped)) return <LoadingState className="py-32" />;
  if (dash.error) return <ErrorState error={dash.error} onRetry={dash.refetch} />;
  if (d?.scoped) {
    return (
      <div className="page-pad fade-up" data-testid="scoped-dashboard">
        <h1 className="text-[20px] font-semibold leading-7 tracking-tight">Hei, {user?.name?.split(" ")[0]}</h1>
        <p className="mt-0.5 text-[13px] text-muted-foreground">Du har tilgang til {d.projects.length === 1 ? "ett prosjekt" : `${d.projects.length} prosjekter`}.</p>
        <ul className="mt-6 divide-y divide-border/70">
          {d.projects.map((p) => (
            <li key={p.id}>
              <button type="button" onClick={() => navigate(`/prosjekter/${p.id}`)} className="-mx-2 flex w-[calc(100%+1rem)] items-center justify-between gap-4 rounded-lg px-2 py-3 text-left transition-colors duration-150 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none" data-testid={`scoped-project-${p.id}`}>
                <span className="min-w-0">
                  <span className="block truncate text-[14px] font-medium">{p.name}</span>
                  <span className="block text-[12px] text-muted-foreground">{p.project_number} · {p.address || ""}</span>
                </span>
                <span className="shrink-0 text-[12px] text-muted-foreground tabular">{p.capacity_persons ? `${p.capacity_persons} personer` : ""}</span>
              </button>
            </li>
          ))}
        </ul>
        {can("innkvartering:read") && <ScopedCamps />}
      </div>
    );
  }

  const hour = new Date().getHours();
  const greet = hour < 10 ? "God morgen" : hour < 17 ? "God dag" : "God kveld";
  const firstName = user?.name?.split(" ")[0];
  const live = m?.live || {};
  const attention = d.attention || { items: [], total: 0, by_severity: {} };
  const selected = m?.locations?.find((l) => l.id === selectedId) || null;
  const projectSites = (m?.locations || []).filter((l) => l.kind === "project_site").length;
  const depots = (m?.locations || []).filter((l) => l.kind === "depot").length;

  const locate = (projectId) => {
    const loc = m?.locations?.find((l) => l.project?.id === projectId);
    if (!loc) return;
    setSelectedId(loc.id);
    setFocus((f) => ({ loc, tick: f.tick + 1 }));
    if (!wide) document.querySelector('[data-testid="dashboard-map"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const stats = [
    { id: "projects", icon: FolderKanban, label: "Aktive prosjekt", value: num(live.projects_active ?? d.projects.active), sub: `${d.projects.starting_30d} starter neste 30 dager`, onClick: () => navigate("/prosjekter") },
    { id: "modules-out", icon: Boxes, label: "Moduler ute", value: num(d.modules.rented), sub: `${pct(d.modules.utilization_pct, 0)} utnyttelse · ${num(d.modules.total)} i flåten`, onClick: () => navigate("/moduler") },
    { id: "in-transit", icon: Truck, tone: "primary", label: "Moduler på vei", value: num(live.modules_in_transit ?? d.modules.by_status.transport ?? 0), sub: `${live.in_transit ?? 0} transporter · ${live.deliveries_today ?? 0} leveranser i dag`, onClick: () => navigate("/kart") },
    { id: "attention", icon: AlertTriangle, label: "Krever handling", value: num(attention.total), sub: attention.total ? `${attention.by_severity.danger || 0} kritiske · ${d.conflicts.count} konflikter` : "Alt under kontroll", tone: attention.by_severity?.danger ? "danger" : attention.total ? "warning" : undefined, onClick: () => navigate("/service") },
  ];

  const panel = selected ? (
    <div className="h-full overflow-hidden rounded-2xl bg-card/95 shadow-pop backdrop-blur-xl fade-up" data-testid="dashboard-selected-panel">
      <MapPreviewCard loc={selected} onClose={() => setSelectedId(null)} variant="panel" />
    </div>
  ) : (
    <PriorityPanel attention={attention} priority={brief.data?.priority} priorityLoading={brief.isLoading} onLocate={m ? locate : undefined} locatable={locatable} variant="floating" />
  );

  return (
    <div className="page-pad pb-12 fade-up" data-testid="dashboard-page">
      {/* Executive header */}
      <header className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between" data-testid="dashboard-hero">
        <div className="min-w-0 max-w-3xl">
          <p className="text-[12.5px] leading-4 text-muted-foreground" data-testid="dashboard-date">
            <span className="first-letter:uppercase">{fmtDate(d.today, "EEEE d. MMMM yyyy")}</span>
            <span className="mx-1.5 text-muted-foreground/60">·</span>
            <span data-testid="dashboard-greeting">
              {greet}, {firstName}
            </span>
          </p>
          <h1 className="mt-1.5 text-[26px] font-semibold leading-8 tracking-tight sm:text-[30px] sm:leading-9">Driftsoversikt</h1>
          <AiBriefLine brief={brief} className="mt-3" />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {can("planning:read") && (
            <Button variant="outline" onClick={() => navigate("/planlegging")} data-testid="dashboard-planning-button">
              Planlegging <ArrowRight />
            </Button>
          )}
          {can("leads:write") && (
            <Button onClick={() => navigate("/henvendelser?ny=1")} data-testid="dashboard-new-lead-button">
              <Plus /> Ny henvendelse
            </Button>
          )}
        </div>
      </header>

      <StatStrip items={stats} className="mt-8" />

      {/* Map hero with floating priority panel */}
      <div className="relative mt-8 h-[440px] overflow-hidden rounded-2xl ring-1 ring-black/[0.06] dark:ring-white/10 sm:h-[540px] xl:h-[clamp(580px,calc(100vh_-_15rem),800px)]" data-testid="dashboard-map">
        {m ? (
          <PortfolioMap
            data={m}
            selectedId={selectedId}
            onSelect={setSelectedId}
            layer={layer}
            onLayerChange={changeLayer}
            legend={false}
            fitPadding={wide ? { top: 80, bottom: 72, left: 80, right: 480 } : { top: 72, bottom: 64, left: 40, right: 40 }}
            topLeft={
              <MapLinkChip onClick={() => navigate("/kart")} testId="dashboard-open-map">
                Åpne kart
              </MapLinkChip>
            }
          >
            <MapVignette />
            <CameraFocus target={focus.loc} tick={focus.tick} offsetX={wide ? -220 : 0} />
            {wide && (
              <div className="pointer-events-none absolute inset-y-4 right-4 z-20 w-[408px]" data-testid="dashboard-map-panel">
                <div className="pointer-events-auto h-full">{panel}</div>
              </div>
            )}
            {!wide && selected && (
              <div className="pointer-events-none absolute bottom-3 left-3 z-20 max-w-full">
                <MapPreviewCard loc={selected} onClose={() => setSelectedId(null)} />
              </div>
            )}
            <div className="pointer-events-none absolute bottom-3 left-3 z-10 hidden items-center gap-2 sm:flex">
              <MapLegend items={LEGEND} />
              <div className="rounded-lg bg-card/80 px-2.5 py-1.5 text-[11.5px] leading-4 text-muted-foreground backdrop-blur" data-testid="dashboard-map-hint">
                {projectSites} prosjekttomter · {depots} depot · {m.transports.length} planlagte transporter · Kjent lokasjon, ikke sanntid
              </div>
            </div>
          </PortfolioMap>
        ) : (
          <div className="flex h-full items-center justify-center text-[12.5px] text-muted-foreground">Kartet krever prosjekttilgang.</div>
        )}
      </div>

      {!wide && <PriorityPanel attention={attention} priority={brief.data?.priority} priorityLoading={brief.isLoading} onLocate={m ? locate : undefined} locatable={locatable} variant="section" className="mt-6" />}

      {/* The week as an operations board */}
      <section className="mt-10" data-testid="dashboard-agenda">
        <header className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-[15px] font-semibold leading-5 tracking-tight">Uken framover</h2>
            <p className="mt-0.5 text-[12px] text-muted-foreground" data-testid="dashboard-agenda-meta">
              {weekEvents.length ? `${weekEvents.length} hendelser · transport, feltarbeid, milepæler og frister – én linje per prosjekt` : "Ingen planlagte hendelser de neste sju dagene"}
            </p>
          </div>
          <QuietLink onClick={() => navigate("/planlegging")} testId="dashboard-agenda-planning-link">
            Åpne planlegging
          </QuietLink>
        </header>
        <WeekTimeline today={d.today} events={weekEvents} />
      </section>

      {/* Position */}
      <div className="mt-10 grid gap-10 lg:grid-cols-3 lg:gap-8">
        <FleetSummary modules={d.modules} />
        <CapacitySummary cap={d.capacity_outlook} divided />
        <PipelineSummary leads={d.leads} quotes={d.quotes} divided />
      </div>
    </div>
  );
}
